import React, { useMemo } from "react";
import _ from "lodash";
import { Field, useField } from "react-final-form";
import {
  FormControlLabel,
  FormControlLabelProps,
  Radio,
  RadioGroup as RadioGroupInternal,
  RadioGroupProps as RadioGroupInternalProps,
  RadioProps,
  Tooltip,
} from "@mui/material";
import clsx from "clsx";

type RadioControlProps = {
  value?: unknown;
  loading?: boolean;
  disabled?: boolean;
  disabledTooltip?: string | React.ReactNode;
  label: string | React.ReactNode;
  recommended?: boolean;
  narrow?: boolean;
  formControlProps?: Partial<FormControlLabelProps>;
  radioProps?: Partial<RadioProps>;
  field: string;
  hideLabel?: boolean /* for use in a grid of options in a table, where the labels are already on the table header */;
};

export const RadioControlLabel: React.FC<RadioControlProps> = ({
  value,
  loading = false,
  disabled = false,
  label,
  recommended,
  narrow,
  formControlProps,
  radioProps,
  field,
  hideLabel,
}) => {
  const controlProps = useMemo(() => {
    const controlClasses = clsx({
      "mr-xs": narrow,
    });
    return _.merge({ className: controlClasses }, formControlProps);
  }, [narrow, formControlProps]);

  return (
    <FormControlLabel
      value={value}
      style={hideLabel ? { marginRight: "0" } : {}}
      control={
        <Field
          name={field}
          value={value}
          type="radio"
          render={({ input }) => (
            <Radio
              color="primary"
              {...radioProps}
              {...input}
              disabled={radioProps?.disabled || disabled || loading}
            />
          )}
        />
      }
      label={
        <div className={clsx(hideLabel ? "sr-only" : "d-flex")}>
          <span>{label}</span>
          {recommended && (
            <span
              className="text-color-primary ml-xs"
              style={{ fontSize: "12pt", fontVariant: "small-caps", display: "block" }}
            >
              recommended
            </span>
          )}
        </div>
      }
      {...controlProps}
      disabled={formControlProps?.disabled || disabled || loading}
    />
  );
};

export const RadioGroup: React.FC<RadioGroupProps> = ({
  field,
  options,
  tableRowMode,
  fieldToLabel,
}: RadioGroupProps) => {
  const f = useField(field, { type: "radio" });
  return tableRowMode ? (
    <tr>
      <td>{fieldToLabel ? fieldToLabel[field] : null}</td>
      {options.map((option) => (
        <td key={option.value.toString()} className="text-center">
          <RadioControlLabel {...option} field={field} hideLabel />
        </td>
      ))}
    </tr>
  ) : (
    <RadioGroupInternal
      name={field}
      value={f.input.value}
      onChange={f.input.onChange}
      className={clsx(options.length === 1 && "align-items-center")}
    >
      {options.map((option) =>
        option.disabledTooltip ? (
          <Tooltip key={option.value.toString()} title={option.disabledTooltip} arrow>
            <span>
              <RadioControlLabel key={option.value.toString()} {...option} field={field} disabled />
            </span>
          </Tooltip>
        ) : (
          <RadioControlLabel key={option.value.toString()} {...option} field={field} />
        ),
      )}
    </RadioGroupInternal>
  );
};

export interface RadioGroupProps extends RadioGroupInternalProps {
  field: string;
  options: {
    label: string;
    value: string;
    recommended?: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
  }[];
  tableRowMode?: boolean /* For each Option, emit a <td></td> with just a radio button in it (no label) - this component is part of a larger table. */;
  fieldToLabel?: Record<
    string,
    string
  > /* Technical field name to human-readable label function. */;
}
